/**
 * RecourseOptions Component
 *
 * Displays alternative assignment options when patient cannot be assigned
 */

import { Box, Button, Typography } from '@mui/material';
import { AlternativeAssignmentOptions } from '../types/intake.types';

interface RecourseOptionsProps {
  patientId: number;
  alternatives: AlternativeAssignmentOptions;
  onAssignAlternative: (patientId: number, date: Date, providerName?: string) => void;
  onAssignNextBest: (patientId: number, providerId: number, providerName: string) => void;
  onCreateProvisional: (patientId: number, date: Date) => void;
}

export default function RecourseOptions({
  patientId,
  alternatives,
  onAssignAlternative,
  onAssignNextBest,
  onCreateProvisional,
}: RecourseOptionsProps) {
  const { alternativeDates, nextBestProviders, provisionalOption } = alternatives;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'error.main' }}>
        Recourse Options
      </Typography>

      {/* Alternative Dates */}
      {alternativeDates.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
            Alternative Dates
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {alternativeDates.map((alt, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {alt.date.toLocaleDateString()} ({alt.daysOffset > 0 ? '+' : ''}{alt.daysOffset} days)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {alt.availableProvidersCount} provider{alt.availableProvidersCount !== 1 ? 's' : ''} available
                      {alt.bestProviderName && ` • Best: ${alt.bestProviderName}`}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onAssignAlternative(patientId, alt.date, alt.bestProviderName)}
                  >
                    Assign
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Next Best Providers */}
      {nextBestProviders.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
            Next Best Providers (with warnings)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {nextBestProviders.map((provider, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'warning.main',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {provider.providerName} ({provider.territory})
                    </Typography>
                    <Typography variant="caption" color="warning.dark" display="block">
                      ⚠ {provider.warning}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {provider.utilizationPercent}% utilized • {provider.availableMinutes} min available
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => onAssignNextBest(patientId, provider.providerId, provider.providerName)}
                  >
                    Assign
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Provisional/Marketplace Option */}
      {provisionalOption && (
        <Box>
          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
            Marketplace Option
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'white',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.main',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {provisionalOption.suggestedAction}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {provisionalOption.description}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={() => onCreateProvisional(patientId, provisionalOption.intakeVisitDate)}
              >
                Create
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
